import { FileNameSeparatorWithExtension } from '@/components/FileNameSeparatorWithExtension';
import { usePictureInPictureModeContext } from '@/features/approving_invoice_reports/components/List/context/PictureInPictureModeProvider';
import { ActiveFileType } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import { useInvoiceFiles } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/useInvoiceFiles';
import { TextLink } from '@moneyforward/ap-frontend-components';
import { ApprovingInvoiceReportFormInputResponse } from 'ap-openapi';
import classNames from 'classnames/bind';
import { FC, memo, useCallback } from 'react';
import styles from '../../styles.module.scss';

const cx = classNames.bind(styles);

type InvoiceFileFieldViewProps = {
  item: ApprovingInvoiceReportFormInputResponse;
  onSelectFile?: () => void;
};

export const InvoiceFileFieldView: FC<InvoiceFileFieldViewProps> = memo(
  ({ item, onSelectFile }) => {
    const baseURL = location.origin;
    const { isPIPMode } = usePictureInPictureModeContext();
    // Use the new useInvoiceFiles hook that includes:
    // - Automatically set invoice file as active on mount (default behavior)
    // - Watch for tab changes and activate invoice file when invoice tab is selected
    const {
      processedFile,
      selectInvoiceFile: selectInvoiceFileFromHook,
      activeFile,
    } = useInvoiceFiles(item, baseURL);

    // Create selection function with callback
    const selectInvoiceFile = useCallback(() => {
      selectInvoiceFileFromHook();
      onSelectFile?.();
    }, [selectInvoiceFileFromHook, onSelectFile]);

    // Create click handler for invoice file selection
    const handleInvoiceClick = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        if (isPIPMode) return;
        selectInvoiceFile();
      },
      [selectInvoiceFile, isPIPMode]
    );

    // Check if this invoice file is currently active
    const isActive =
      isPIPMode ||
      (activeFile?.type === ActiveFileType.INVOICE &&
        activeFile?.id === processedFile?.id);

    if (!processedFile) {
      return null;
    }

    return (
      <div
        className={cx(
          'linkContainerReport',
          !processedFile.id && 'emptyViewInvoiceFileField',
          isActive && 'active'
        )}
        key={processedFile?.id ?? 'empty-view-invoice-file-field'}
        onClick={handleInvoiceClick}
      >
        <div
          className={cx(
            `textLinkContainer`,
            `${
              !processedFile?.invoice_number
                ? 'textLinkContainerWithUserSelect'
                : ''
            }`
          )}
        >
          <FileNameSeparatorWithExtension>
            {processedFile?.name ?? ''}
          </FileNameSeparatorWithExtension>
          <div className={cx('textLinkContainerExtension')}>
            {processedFile?.invoice_number && (
              <TextLink
                href={processedFile.href}
                target='_blank'
                onClick={(e) => {
                  e.stopPropagation();
                  selectInvoiceFile();
                }}
              >
                {processedFile.invoice_number}
              </TextLink>
            )}
          </div>
        </div>
      </div>
    );
  }
);

InvoiceFileFieldView.displayName = 'InvoiceFileFieldView';
